<template>
  <div class="mb-4">
    <h6 class="fw-bold mb-3">
      {{ activityName }}
    </h6>

    <card-info-section
      :card-number="professionalCardNumber"
      :delivery-city="deliveryCity"
      :delivery-date="deliveryDate"
      :touched-fields="touchedFields"
      @update:card-number="professionalCardNumber = $event"
      @update:delivery-city="deliveryCity = $event"
      @update:delivery-date="deliveryDate = $event"
      @touch="handleTouch"
    />

    <approver-item
      v-for="(approver, index) in localApprovers"
      :key="index"
      :approver="approver"
      :index="index"
      :touched-approver="touchedApprovers[index]"
      :position-list="positionList"
      @update:first-name="updateApproverField(index, 'approverFirstName', $event)"
      @update:last-name="updateApproverField(index, 'approverLastName', $event)"
      @update:position="updateLocalApproverPosition(index, $event)"
      @touch="setApproverTouched(index, $event)"
      @remove="removeApprover(index)"
    />

    <button
      class="btn btn-outline-primary w-100 mb-3"
      type="button"
      @click="addApprover"
    >
      Ajouter un titulaire
    </button>

    <activities-section
      :activity-choices="activityChoices"
      :local-activities="localActivities"
      @update="updateLocalActivities"
    />
  </div>
</template>

<script>
import { isArray } from 'lodash-es';

import CardInfoSection from './ProfessionalCard/CardInfoSection.vue';
import ApproverItem from './ProfessionalCard/ApproverItem.vue';
import ActivitiesSection from './ProfessionalCard/ActivitiesSection.vue';
import positionList from '@/assets/data/position.json';
import activityList from '@/assets/data/cardProActivites.json';

export default {
  name: 'ComplementaryProfessionalCardForm',
  components: {
    CardInfoSection,
    ApproverItem,
    ActivitiesSection,
  },
  props: {
    contract: { type: Object, required: true },
    activityName: { type: String, default: 'Activité' },
  },
  emits: ['validation'],
  data() {
    return {
      professionalCardNumber: '',
      deliveryCity: '',
      deliveryDate: '',
      touchedFields: { professionalCardNumber: false, deliveryCity: false, deliveryDate: false },
      touchedApprovers: {},
      localApprovers: [],
      localActivities: [],
      positionList,
      activityChoices: activityList,
    };
  },
  computed: {
    isProfessionalCardNumberValid() {
      return this.professionalCardNumber.length > 0 && /^.{1,20}$/.test(this.professionalCardNumber.replace(/\s/g, ''));
    },
    isDeliveryCityValid() {
      return this.deliveryCity.trim().length > 0;
    },
    isDeliveryDateValid() {
      if (!this.deliveryDate) return false;
      const selectedDate = new Date(this.deliveryDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selectedDate >= new Date('1900-01-01') && selectedDate <= today;
    },
    areApproversValid() {
      if (!this.localApprovers || this.localApprovers.length === 0) return false;
      return this.localApprovers.every(a => a.approverFirstName?.trim() && a.approverLastName?.trim() && a.position?.code && a.position?.label);
    },
    areActivitiesValid() {
      return this.localActivities?.length > 0;
    },
    isFormValid() {
      return this.isProfessionalCardNumberValid && this.isDeliveryCityValid && this.isDeliveryDateValid
        && this.areApproversValid && this.areActivitiesValid;
    },
    hasError() {
      return !this.isFormValid;
    },
  },
  watch: {
    hasError: {
      immediate: true,
      handler(val) {
        this.$emit('validation', !val);
      },
    },
  },
  mounted() {
    const card = this.contract?.risk?.professionalCard || {};
    this.professionalCardNumber = card.cardNumber || '';
    this.deliveryCity = card.deliveryCity || '';
    this.deliveryDate = this.formatDateForInput(card.deliveryDate);
    this.localActivities = card.activities || [];
    const approvers = card.approvers || [{ approverFirstName: '', approverLastName: '', position: null }];
    this.localApprovers = approvers.map((a) => ({ ...a, position: a.position || null }));
  },
  methods: {
    formatDateForInput(value) {
      if (isArray(value)) {
        const [year, month, day] = value;
        return [
          year,
          String(month).padStart(2, '0'),
          String(day).padStart(2, '0'),
        ].join('-');
      }

      return value || '';
    },
    formatDateForBackend(value) {
      return value.split('-').map(Number);
    },
    handleTouch(field) {
      this.touchedFields[field] = true;
    },
    setApproverTouched(index, field) {
      if (!this.touchedApprovers[index]) {
        this.$set(this.touchedApprovers, index, { firstName: false, lastName: false });
      }
      this.$set(this.touchedApprovers[index], field, true);
    },
    updateApproverField(index, field, value) {
      this.$set(this.localApprovers[index], field, value);
    },
    updateLocalApproverPosition(index, position) {
      this.$set(this.localApprovers[index], 'position', position);
    },
    updateLocalActivities(activities) {
      this.localActivities = activities;
    },
    addApprover() {
      this.localApprovers.push({ approverFirstName: '', approverLastName: '', position: null });
    },
    removeApprover(i) {
      this.localApprovers.splice(i, 1);
    },
    formatApproverForBackend(a) {
      return {
        approverFirstName: a.approverFirstName,
        approverLastName: a.approverLastName,
        position: a.position?.code ? { code: a.position.code, label: a.position.label } : null,
      };
    },
    async submit() {
      const formattedApprovers = this.localApprovers.map(this.formatApproverForBackend);

      return {
        professionalCard: {
          approvers: formattedApprovers,
          activities: this.localActivities,
          cardNumber: this.professionalCardNumber,
          deliveryCity: this.deliveryCity,
          deliveryDate: this.formatDateForBackend(this.deliveryDate),
        },
      };
    },
  },
};
</script>
